import type React from 'react';
import type { StateNexusData } from '../services/api';
import ProgressBar from './ProgressBar';

interface LevelConfig {
  label: string;
  color: string;
  bg: string;
  border: string;
}

const LEVEL_CONFIG: Record<80 | 90 | 100, LevelConfig> = {
  80: { label: 'Warning', color: '#E97135', bg: '#FFF1EB', border: '#E97135' },
  90: { label: 'Critical', color: '#E97135', bg: '#FFF1EB', border: '#E97135' },
  100: { label: 'Triggered', color: '#E93544', bg: '#FFEBEC', border: '#E93544' },
};

function alertLevelFor(s: StateNexusData): 80 | 90 | 100 {
  if (s.nexusTriggered) return 100;
  if (s.revenueThresholdPct >= 90) return 90;
  return 80;
}

function fmtThreshold(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function deriveNote(s: StateNexusData): string {
  if (s.nexusTriggered) {
    return s.registrationStatus === 'registered' ? 'Return ready' : 'Register required';
  }
  return 'Approaching threshold';
}

const TH: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
};

function SkRow(): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 18px',
        borderBottom: '1px solid #F1F5F9',
      }}
    >
      {['state', 'status', 'threshold', 'progress', 'notes'].map((col, i) => (
        <div
          key={col}
          className="skeleton-box"
          style={{
            flex: i < 4 ? `0 0 ${[160, 120, 100, 170][i]}px` : 1,
            height: 12,
            marginRight: 10,
          }}
        />
      ))}
      <div style={{ width: 100 }} />
    </div>
  );
}

interface AlertRowProps {
  state: StateNexusData;
  onDismiss: (stateCode: string) => void;
}

function AlertRow({ state, onDismiss }: AlertRowProps): JSX.Element {
  const cfg = LEVEL_CONFIG[alertLevelFor(state)];
  const pct = state.revenueThresholdPct ?? 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 18px',
        borderBottom: '1px solid #F1F5F9',
        background: '#fff',
      }}
    >
      <div style={{ flex: '0 0 160px', fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
        {state.stateName}
      </div>
      <div style={{ flex: '0 0 120px' }}>
        <span
          style={{
            background: cfg.bg,
            border: `1px solid ${cfg.border}`,
            color: cfg.color,
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {cfg.label}
        </span>
      </div>
      <span style={{ flex: '0 0 100px', fontSize: 12, color: '#475569' }}>
        {fmtThreshold(state.revenueThreshold)}
      </span>
      <div style={{ flex: '0 0 170px', paddingRight: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: cfg.color }}>{pct.toFixed(0)}%</div>
        <ProgressBar pct={pct} color={cfg.color} height={6} margin="4px 0 0" />
      </div>
      <span style={{ flex: 1, fontSize: 12, color: '#64748B' }}>{deriveNote(state)}</span>
      <div style={{ width: 100, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => onDismiss(state.stateCode)}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#64748B',
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 7,
            padding: '6px 14px',
            cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export interface NexusAlertsTableProps {
  states: StateNexusData[];
  dismissedCodes: Set<string>;
  loading: boolean;
  error: string | null;
  errorStatus?: number | null;
  onRefresh: () => void;
  onDismiss: (stateCode: string) => void;
}

export default function NexusAlertsTable({
  states,
  dismissedCodes,
  loading,
  error,
  errorStatus,
  onRefresh,
  onDismiss,
}: NexusAlertsTableProps): JSX.Element {
  const forbidden = errorStatus === 403;
  const rows = states
    .filter(
      (s) => (s.nexusTriggered || s.revenueThresholdPct >= 80) && !dismissedCodes.has(s.stateCode),
    )
    .sort((a, b) => b.revenueThresholdPct - a.revenueThresholdPct);

  return (
    <div>
      <div
        style={{
          background: '#fff',
          border: '1px solid #E2E8F0',
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '13px 18px',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>
            Nexus threshold alerts
          </span>
          <button
            type="button"
            onClick={onRefresh}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 13px',
              background: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            <svg
              aria-hidden="true"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Refresh
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 700 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#F8FAFC',
                padding: '10px 18px',
                borderBottom: '1px solid #E2E8F0',
              }}
            >
              <span style={{ flex: '0 0 160px', ...TH }}>State</span>
              <span style={{ flex: '0 0 120px', ...TH }}>Status</span>
              <span style={{ flex: '0 0 100px', ...TH }}>Threshold</span>
              <span style={{ flex: '0 0 170px', ...TH }}>Progress</span>
              <span style={{ flex: 1, ...TH }}>Notes</span>
              <span style={{ width: 100, ...TH }}>Actions</span>
            </div>

            {loading ? (
              <>
                <SkRow />
                <SkRow />
                <SkRow />
              </>
            ) : error ? (
              <div
                style={{
                  padding: '24px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: forbidden ? '#C2410C' : '#BE123C',
                    fontWeight: 600,
                  }}
                >
                  {forbidden ? "You don't have permission to view this" : 'Could not load alerts'}
                </span>
                <span style={{ fontSize: 11, color: '#94A3B8' }}>
                  {forbidden ? 'Ask your organization admin for access.' : error}
                </span>
                {!forbidden && (
                  <button
                    type="button"
                    onClick={onRefresh}
                    style={{
                      marginTop: 4,
                      padding: '6px 14px',
                      background: '#0F172A',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 7,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Try again
                  </button>
                )}
              </div>
            ) : rows.length === 0 ? (
              <div
                style={{
                  padding: '32px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <svg aria-hidden="true" width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="14" stroke="#BBF7D0" strokeWidth="1.5" />
                  <path
                    d="M10 16l4 4 8-8"
                    stroke="#16A34A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span style={{ fontSize: 13, color: '#64748B', fontWeight: 500 }}>
                  No active alerts
                </span>
                <span style={{ fontSize: 11, color: '#94A3B8' }}>You're all caught up</span>
              </div>
            ) : (
              rows.map((s) => <AlertRow key={s.stateCode} state={s} onDismiss={onDismiss} />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
